/* eslint-disable no-undef */
if (!customElements.get('product-images')) {
  class ProductImages extends HTMLElement {
    constructor () {
      super()
      this.thumbnailClickListeners = []
      this.navigationClickListeners = {}
      this.videoTimeouts = {}
    }

    connectedCallback () {
      if (this.dataset.productMediaSize === '1') return
      if (EmblaCarousel) {
        this.firstTimeLoad = localStorage.getItem(`firstTimeLoad-${this.dataset.productId}`) === null

        this.loading = true
        this.initCarousel().then(() => {
          this.loading = false // Set loading to false after initialization
        })
      }

      const variantImages = this.variantImages[this.initialVariantId] || []
      if (variantImages.length > 0) {
        this.initialLoadWithAdditionalImages(variantImages)
      }

      this.addEventListener('variantChange', this.onVariantChange.bind(this))
    }

    async initCarousel () {
      this.mainOptions = {
        speed: 15,
        containScroll: 'trimSnaps',
        loop: true,
      }

      this.thumbOptions = {
        containScroll: 'keepSnaps',
        dragFree: true,
        loop: false,
        align: 'center',
        breakpoints: {
          '(min-width: 1280px)': {
            active: false,
          },
        },
      }

      await this.setupCarousels().then(() => {
        this.setupThumbnailClick()
        if (this.firstTimeLoad) {
          localStorage.setItem(`firstTimeLoad-${this.dataset.productId}`, 'false')
          this.handleFirstTimeLoad() // Handle first time load after setup
        }
      })
    }

    async initialLoadWithAdditionalImages (images) {
      const variantId = this.initialVariantId

      this.featuredMedia.forEach((featuredMedia) => {
        featuredMedia.classList.toggle('tb-hidden', featuredMedia.dataset.variantId !== variantId)
      })

      this.removeAdditionalImages()
      await this.addAdditionalImages(variantId, images).then(() => {
        this.reinitializeCarousel()
      })
    }

    async setupCarousels () {
      this.mainEmbla = EmblaCarousel(this.querySelector('.product-carousel__viewport'), this.mainOptions)
      this.thumbsEmbla = EmblaCarousel(this.querySelector('.product-carousel-thumbs__viewport'), this.thumbOptions)
      this.setupNavigation()

      // Initialize video control for the first slide
      this.controlSlideVideos(this.mainEmbla.selectedScrollSnap())
    }

    setupNavigation () {
      const prevButtonNode = this.querySelector('.product-carousel__prev')
      const nextButtonNode = this.querySelector('.product-carousel__next')

      if (prevButtonNode) {
        const prevListener = this.handlePrevClick.bind(this)
        prevButtonNode.addEventListener('click', prevListener, false)
        this.navigationClickListeners.prev = { node: prevButtonNode, listener: prevListener }
      }

      if (nextButtonNode) {
        const nextListener = this.handleNextClick.bind(this)
        nextButtonNode.addEventListener('click', nextListener, false)
        this.navigationClickListeners.next = { node: nextButtonNode, listener: nextListener }
      }

      // On embla drag, update selected thumbnail
      this.mainEmbla.on('select', () => {
        if (window.innerWidth < 1280) {
          this.thumbsEmbla.scrollTo(this.mainEmbla.selectedScrollSnap())
        }
        this.setSelectedThumbnail(this.mainEmbla.selectedScrollSnap())
        this.trackSlideChange(this.mainEmbla.selectedScrollSnap())

        // Control video playback when slides change
        this.controlSlideVideos(this.mainEmbla.selectedScrollSnap())
      })
    }

    controlSlideVideos (activeIndex) {
      // If we have any existing delay timeouts, clear them
      Object.values(this.videoTimeouts).forEach(timeout => clearTimeout(timeout))
      this.videoTimeouts = {}

      const slides = this.mainEmbla.slideNodes()
      slides.forEach((slide, index) => {
        const video = slide.querySelector('video')
        if (!video) return

        const slideId = `video-${index}`

        if (index === activeIndex) {
          // Play video on active slide
          if (video.paused) {
            video.play()
          }
        } else {
          // For inactive slides, wait before resetting
          this.videoTimeouts[slideId] = setTimeout(() => {
            // Pause and reset the video
            video.pause()
            video.currentTime = 0
          }, 500) // 500ms delay before resetting
        }
      })
    }

    trackSlideChange (index) {
      console.log('Tracking slide change:', index, typeof gtag !== 'undefined')
      const slide = this.mainEmbla.slideNodes()[index]
      const img = slide.querySelector('img')
      if (!img) return

      const src = img.src
      const fileName = src.split('/').pop().split('?')[0].split('.')[0]
      const eventName = `product-carousel_${fileName}`

      if (typeof gtag !== 'undefined') {
        gtag('event', eventName)
        console.log('Tracked carousel slide change:', eventName)
      }
    }

    removeNavigationListeners () {
      if (this.navigationClickListeners.prev) {
        const { node, listener } = this.navigationClickListeners.prev
        node.removeEventListener('click', listener)
      }
      if (this.navigationClickListeners.next) {
        const { node, listener } = this.navigationClickListeners.next
        node.removeEventListener('click', listener)
      }
      this.navigationClickListeners = {}
    }

    setupThumbnailClick () {
      const thumbsNodes = this.thumbsEmbla.slideNodes()

      thumbsNodes.forEach((thumb, index) => {
        const listener = () => {
          this.setSelectedThumbnail(index)
          this.mainEmbla.scrollTo(index)
          this.thumbsEmbla.scrollTo(index)
        }

        thumb.addEventListener('click', listener)
        this.thumbnailClickListeners.push({ thumb, listener })
      })
    }

    handleFirstTimeLoad () {
      setTimeout(() => {
        this.reinitializeCarousel() // Reinitialize to ensure everything is set up correctly
      }, 500)
    }

    removeThumbnailClickListeners () {
      this.thumbnailClickListeners.forEach(({ thumb, listener }) => {
        thumb.removeEventListener('click', listener)
      })
      this.thumbnailClickListeners = []
    }

    setSelectedThumbnail (index) {
      const thumbsNodes = this.thumbsEmbla.slideNodes()
      thumbsNodes.forEach(t => t.classList.remove('product-carousel-thumbs__slide--selected'))
      thumbsNodes[index].classList.add('product-carousel-thumbs__slide--selected')
    }

    handlePrevClick () {
      this.mainEmbla.scrollPrev()
      if (window.innerWidth < 1280) {
        this.thumbsEmbla.scrollTo(this.mainEmbla.selectedScrollSnap())
      }
      this.setSelectedThumbnail(this.mainEmbla.selectedScrollSnap())
    }

    handleNextClick () {
      this.mainEmbla.scrollNext()
      if (window.innerWidth < 1280) {
        this.thumbsEmbla.scrollTo(this.mainEmbla.selectedScrollSnap())
      }
      this.setSelectedThumbnail(this.mainEmbla.selectedScrollSnap())
    }

    async onVariantChange (event) {
      const { variant } = event.detail
      const variantId = String(variant.id)
      const variantImages = this.variantImages[variantId] || []

      this.featuredMedia.forEach((featuredMedia) => {
        featuredMedia.classList.toggle('tb-hidden', featuredMedia.dataset.variantId !== variantId)
      })

      this.setSelectedThumbnail(0)

      this.removeAdditionalImages()
      await this.addAdditionalImages(variantId, variantImages).then(() => {
        this.reinitializeCarousel()
      })
    }

    removeAdditionalImages () {
      const additionalSlides = this.querySelectorAll('.product-carousel__slide--additional-image')
      additionalSlides.forEach(slide => slide.remove())

      const additionalThumbs = this.querySelectorAll('.product-carousel-thumbs__slide--additional-image')
      additionalThumbs.forEach(thumb => thumb.remove())
    }

    async addAdditionalImages (variantId, images) {
      const carouselContainer = this.querySelector('.product-carousel__container')
      const thumbsContainer = this.querySelector('.product-carousel-thumbs__container')

      const firstImageSlide = carouselContainer.querySelector('.product-carousel__slide')
      const firstThumbSlide = thumbsContainer.querySelector('.product-carousel-thumbs__slide')
      const imageIndexOffset = 1

      images.forEach((image, index) => {
        const imageIndex = imageIndexOffset + index

        const slide = document.createElement('div')
        slide.classList.add('product-carousel__slide', 'product-carousel__slide--additional-image')
        slide.dataset.variantId = variantId
        slide.dataset.imageIndex = imageIndex
        slide.innerHTML = `<img src="${image}" class="w-full max-w-full rounded-sm bg-sheep object-cover aspect-[4/3]" alt="Additional image">`

        firstImageSlide.insertAdjacentElement('afterend', slide)

        const thumbSlide = document.createElement('div')
        thumbSlide.classList.add('product-carousel-thumbs__slide', 'product-carousel-thumbs__slide--additional-image')
        thumbSlide.dataset.variantId = variantId
        thumbSlide.dataset.imageIndex = imageIndex
        thumbSlide.innerHTML = `<button class="product-carousel-thumbs__slide-button block" type="button">
                                  <img src="${image}" class="w-full max-w-full rounded-sm bg-sheep object-cover aspect-[4/3]" alt="Additional thumbnail">
                                </button>`

        firstThumbSlide.insertAdjacentElement('afterend', thumbSlide)
      })
    }

    async reinitializeCarousel () {
      // Clear any existing video timeouts
      Object.values(this.videoTimeouts).forEach(timeout => clearTimeout(timeout))
      this.videoTimeouts = {}

      this.mainEmbla.destroy()
      this.thumbsEmbla.destroy()

      this.removeThumbnailClickListeners()
      this.removeNavigationListeners()

      this.mainEmbla = null
      this.thumbsEmbla = null

      await this.setupCarousels().then(() => {
        this.setupThumbnailClick()
      })
    }

    disconnectedCallback () {
      // Clear any existing video timeouts
      Object.values(this.videoTimeouts).forEach(timeout => clearTimeout(timeout))
      this.videoTimeouts = {}

      this.removeThumbnailClickListeners()
      this.removeNavigationListeners()
    }

    set loading (value) {
      this.setAttribute('loading', value)
    }

    get loading () {
      return this.getAttribute('loading') === 'true'
    }

    get initialVariantId () {
      return this.dataset.initialVariantId
    }

    get featuredMedia () {
      return this.querySelectorAll('.tb-product-carousel-featured')
    }

    get variantImages () {
      return this.dataset.variantImages ? JSON.parse(this.dataset.variantImages) : {}
    }
  }

  customElements.define('product-images', ProductImages)
}
/* eslint-enable  */
